import { useMemo } from "react";
import { look } from "@bd/art";
import { dateOf } from "@bd/sim";
import { Icon } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { phaseLabel } from "../ui/Hud";
import { CloseupCanvas, type Actor } from "./CloseupCanvas";
import { studio } from "./World";

/** "Zoom in" on a room from the studio: same pixels, lit like a stage. */
export function Closeup({ roomId }: { roomId: string }) {
  const game = useGame((s) => s.game)!;
  const quality = useGame((s) => s.quality);
  const set = useGame((s) => s.set);
  const co = me(game)!;
  const room = co.rooms.find((r) => r.id === roomId);
  const zoomFrom = useMemo(() => (studio ? studio.roomScreen(roomId) : null), [roomId]);
  const kind = room?.type === "playtest_lab" ? "playtest" : "design";
  const testing = co.projects.find((p) => p.phase === "playtest");
  const working = co.projects.find((p) => p.phase !== "ready");
  const actors: Actor[] = useMemo(() => {
    if (kind === "design") {
      const team = co.staff.filter((s) => s.projectId).slice(0, 3);
      return [
        ...team.map((s, i) => ({ id: s.id, look: look(s.look), seat: i, armsUp: false, lean: 0, mouth: "smile" as const })),
        ...(team.some((s) => s.founder) ? [] : [{ id: "you", look: look(co.staff[0]?.look ?? 3), seat: -1, armsUp: false, lean: 0, mouth: "smile" as const }]),
      ];
    }
    return [{ id: "you", look: look(co.staff[0]?.look ?? 3), seat: -1, armsUp: false, lean: 0, mouth: "smile" as const }];
  }, [kind, co.staff]);
  if (!room) return null;
  return (
    <>
      <CloseupCanvas kind={kind} year={dateOf(game.week).year} actors={actors} quality={quality} zoomFrom={zoomFrom} mood="dusk" />
      <div className="closeup-hud" data-testid="closeup-hud">
        <div className="panel pt-title">
          <div className="caps">{room.type === "playtest_lab" ? "Playtest lab" : "Design room"}</div>
          <h2 className="display" style={{ margin: "2px 0", fontSize: "2rem" }}>{kind === "design" ? (working ? working.design.name : "Nothing on the desks") : testing ? testing.design.name : "The table is free"}</h2>
          <div className="muted">
            {kind === "design" ? (working ? `${phaseLabel(working).text} · ${co.staff.filter((s) => s.projectId === working.id).length} people on it` : "No game in progress.") : testing ? "A prototype is ready to test." : "No prototype to test yet."}
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            {testing && kind === "playtest" && <button className="btn primary small" onClick={() => set({ closeup: null, screen: { kind: "playtest", projectId: testing.id } })}>Run a playtest</button>}
            {kind === "design" && !working && <button className="btn primary small" onClick={() => set({ closeup: null, screen: { kind: "design" } })}>Design a game</button>}
            <button className="btn small" onClick={() => set({ closeup: null })}><Icon name="back" size={16} /> Back to the studio</button>
          </div>
        </div>
        <div className="keys"><span><kbd>Esc</kbd> studio</span></div>
      </div>
    </>
  );
}
